import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export const getToday = (): Date => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

// Weeks run Monday → Sunday. getDay() returns Sun=0, Mon=1, ..., Sat=6,
// so (getDay() + 6) % 7 gives an offset from Monday (Mon=0, ..., Sun=6).
export const getWeekStart = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const offset = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - offset);
  return d;
};

export const getWeekEnd = (date: Date): Date => {
  const d = getWeekStart(date);
  d.setDate(d.getDate() + 6);
  return d;
};

const sameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

interface WeekPickerProps {
  selectedWeekStart: Date;
  onWeekSelect: (ws: Date) => void;
  onClose: () => void;
  minDate?: Date;
  weeksCount?: number;
}

const WeekPicker: React.FC<WeekPickerProps> = ({
  selectedWeekStart,
  onWeekSelect,
  onClose,
  minDate,
  weeksCount = 1,
}) => {
  const today = getToday();
  const [currentMonth, setCurrentMonth] = useState(
    new Date(selectedWeekStart.getFullYear(), selectedWeekStart.getMonth(), 1),
  );
  const [hoveredWeekStart, setHoveredWeekStart] = useState<Date | null>(null);

  useEffect(() => {
    setCurrentMonth(new Date(selectedWeekStart.getFullYear(), selectedWeekStart.getMonth(), 1));
  }, [selectedWeekStart]);

  const rangeSpanDays = weeksCount * 7 - 1;
  const selEnd = new Date(selectedWeekStart);
  selEnd.setDate(selEnd.getDate() + rangeSpanDays);

  // Monday-first grid: column 0 = Mon, ..., column 6 = Sun.
  const firstDOW =
    (new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1).getDay() + 6) % 7;
  const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
  const prevMonthDays = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 0).getDate();
  const lastDOW =
    (new Date(currentMonth.getFullYear(), currentMonth.getMonth(), daysInMonth).getDay() + 6) % 7;
  const trailingDays = 6 - lastDOW;

  const canPrev = () => {
    if (!minDate) return true;
    return (
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1) >=
      new Date(minDate.getFullYear(), minDate.getMonth(), 1)
    );
  };

  const isDisabled = (d: Date) => d > today || !!(minDate && d < minDate);
  const inSel = (d: Date) => d >= selectedWeekStart && d <= selEnd;
  const isToday = (d: Date) => sameDay(d, today);

  const inHover = (d: Date): boolean => {
    if (!hoveredWeekStart) return false;
    const hoverEnd = new Date(hoveredWeekStart);
    hoverEnd.setDate(hoverEnd.getDate() + rangeSpanDays);
    return d >= hoveredWeekStart && d <= hoverEnd;
  };

  const isWeekStart = (d: Date): boolean => d.getDay() === 1; // Monday
  const isWeekEnd = (d: Date): boolean => d.getDay() === 0;   // Sunday

  const handleClickDate = (date: Date) => {
    if (isDisabled(date)) return;
    onWeekSelect(getWeekStart(date));
    onClose();
  };

  const handleMouseEnterDate = (date: Date) => {
    if (isDisabled(date)) {
      setHoveredWeekStart(null);
      return;
    }
    setHoveredWeekStart(getWeekStart(date));
  };

  const renderDayCell = (date: Date, isOverflow: boolean) => {
    const disabled = isDisabled(date);
    const selected = inSel(date);
    const hovered = !selected && inHover(date);
    const todayMark = isToday(date);
    const weekStart = isWeekStart(date);
    const weekEnd = isWeekEnd(date);

    const isInRange = selected || hovered;
    const rangeColor = selected ? "#00A9A9" : "#E0F5F5";
    const isFirstCell = weekStart;
    const isLastCell = weekEnd;

    return (
      <div
        key={date.toISOString()}
        className="relative h-8 flex items-center justify-center"
        onMouseEnter={() => handleMouseEnterDate(date)}
      >
        {isInRange && (
          <div
            className={`absolute inset-y-0 ${isFirstCell ? "rounded-l-full" : ""} ${isLastCell ? "rounded-r-full" : ""}`}
            style={{
              backgroundColor: rangeColor,
              left: isFirstCell ? "2px" : "-2px",
              right: isLastCell ? "2px" : "-2px",
            }}
          />
        )}

        <button
          onClick={() => !disabled && handleClickDate(date)}
          disabled={disabled}
          className={`
            relative z-10 h-8 w-8 rounded-full text-sm flex items-center justify-center transition-colors
            ${disabled ? "text-gray-300 cursor-not-allowed" : "cursor-pointer"}
            ${isOverflow && !selected && !hovered ? "text-gray-400" : ""}
            ${selected ? "bg-[#00A9A9] text-white" : ""}
            ${hovered && !selected
              ? isOverflow
                ? "bg-[#B2E8E8] text-[#6BCFCF]"
                : "bg-[#B2E8E8] text-[#00A9A9]"
              : ""}
            ${!disabled && !selected && !hovered ? "hover:bg-[#E0F5F5]" : ""}
            ${todayMark && !selected && !hovered
              ? "border border-[#00A9A9] font-semibold" + (isOverflow ? " text-[#6BCFCF]" : " text-[#00A9A9]")
              : ""}
            ${todayMark && hovered && !selected ? "border border-[#00A9A9] font-semibold" : ""}
          `}
        >
          {date.getDate()}
        </button>
      </div>
    );
  };

  const renderCalendar = () => {
    const cells: React.ReactNode[] = [];

    for (let i = 0; i < firstDOW; i++) {
      const day = prevMonthDays - firstDOW + 1 + i;
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, day);
      date.setHours(0, 0, 0, 0);
      cells.push(renderDayCell(date, true));
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      date.setHours(0, 0, 0, 0);
      cells.push(renderDayCell(date, false));
    }

    for (let i = 1; i <= trailingDays; i++) {
      const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, i);
      date.setHours(0, 0, 0, 0);
      cells.push(renderDayCell(date, true));
    }

    return cells;
  };

  return (
    <div
      className="bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80"
      onMouseLeave={() => setHoveredWeekStart(null)}
    >
      <div className="flex items-center justify-between mb-1">
        <button
          onClick={() =>
            canPrev() &&
            setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))
          }
          disabled={!canPrev()}
          className={`p-1 rounded transition ${!canPrev() ? "opacity-40 cursor-not-allowed" : "hover:bg-gray-100"}`}
          aria-label="Previous month"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <span className="font-semibold text-gray-900">
          {currentMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
        </span>
        <button
          onClick={() =>
            setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))
          }
          className="p-1 rounded transition hover:bg-gray-100"
          aria-label="Next month"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <div key={d} className="h-8 flex items-center justify-center text-xs font-medium text-gray-600">
            {d}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>
    </div>
  );
};

export default WeekPicker;
